import { useEffect, useState } from "react";
import { Landmark, BadgeCheck, Gauge } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtAUD, fmtAUDex } from "@/lib/format";

function Row({ label, value, bold, testId }) {
  return (
    <div className={`flex items-center justify-between gap-5 border-b border-gray-100 py-3 last:border-0 ${bold ? "bg-[#faf9f8] px-3 -mx-3" : ""}`}>
      <span className={`text-sm ${bold ? "font-semibold text-gray-900" : "text-gray-600"}`}>{label}</span>
      <span data-testid={testId} className={`shrink-0 font-mono-data text-sm ${bold ? "font-semibold text-gray-950" : "text-gray-900"}`}>{value}</span>
    </div>
  );
}

function PanelHeading({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <div className="bdx-icon-tile"><Icon size={16} strokeWidth={1.9} /></div>
      <h2 className="font-heading text-base font-bold text-gray-950">{title}</h2>
    </div>
  );
}

export default function FinancialsTab({ project }) {
  const [fin, setFin] = useState(null);

  useEffect(() => {
    api.get(`/projects/${project.id}/financials`).then((r) => setFin(r.data));
  }, [project.id]);

  if (!fin) return <div data-testid="financials-loading" className="text-sm text-gray-500">Loading financials…</div>;

  return (
    <div data-testid="financials-tab" className="bdx-page-enter grid grid-cols-1 gap-5 xl:grid-cols-3">
      <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-[0_3px_12px_rgba(16,19,21,0.055)] xl:col-span-2">
        <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <PanelHeading icon={Landmark} title="Project Financial Summary" />
          <span className="self-start rounded-full border border-[#d9cde5] bg-[#f3eff7] px-3 py-1.5 text-[9px] font-semibold uppercase text-[#604184] sm:self-auto" style={{ letterSpacing: "0.1em" }} data-testid="fin-gst-basis-chip">All values Excl. GST</span>
        </div>
        <Row testId="fin-base" label="Original Base Contract Value" value={fmtAUDex(fin.original_base_contract)} />
        <Row testId="fin-approved-vars" label={`Approved Variations (${fin.approved_variations_count})`} value={fmtAUDex(fin.approved_variations_total)} />
        <Row testId="fin-current-total" label="Current Approved Contract Total" value={fmtAUDex(fin.current_approved_contract_total)} bold />
        <Row testId="fin-base-pct" label="Base Contract Claimed %" value={`${fin.base_contract_claimed_pct}%`} />
        <Row testId="fin-base-claimed" label="Base Contract Claimed Value" value={fmtAUDex(fin.base_contract_claimed_value)} />
        <Row testId="fin-vars-claimed" label={`Variations Claimed (${fin.variations_claimed_count})`} value={fmtAUDex(fin.variations_claimed_total)} />
        <Row testId="fin-total-claimed" label="Total Claimed" value={fmtAUDex(fin.total_claimed)} bold />
        <Row testId="fin-remaining" label="Remaining Contract Value" value={fmtAUDex(fin.remaining_contract_value)} bold />
        <Row testId="fin-payments-received" label="Payments Received" value={fmtAUDex(fin.payments_received_total)} />
        <Row testId="fin-payments-outstanding" label="Payments Outstanding (sent, unpaid)" value={fmtAUDex(fin.payments_outstanding_total)} />
        <p className="mt-4 text-[11px] text-gray-500">GST component of total claimed (informational, calculated as GST Inc ÷ 11): {fmtAUD(fin.gst_component_of_total_claimed)}</p>
      </section>

      <div className="space-y-5">
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <PanelHeading icon={BadgeCheck} title="Commercial Status" />
          <div className="mt-4">
            {fin.commercially_complete ? (
              <StatusBadge status="Commercially Complete" tone="green" testId="fin-commercial-status" />
            ) : (
              <StatusBadge status="In Progress" tone="amber" testId="fin-commercial-status" />
            )}
          </div>
          <ul className="mt-4 space-y-2 text-xs text-gray-600">
            <li className="flex items-center gap-2"><span className={`h-1.5 w-1.5 rounded-full ${fin.base_contract_claimed_pct >= 100 ? "bg-emerald-500" : "bg-gray-300"}`} />Base Contract 100% claimed ({fin.base_contract_claimed_pct}%)</li>
            <li className="flex items-center gap-2"><span className={`h-1.5 w-1.5 rounded-full ${fin.variations_unclaimed_count === 0 ? "bg-emerald-500" : "bg-gray-300"}`} />All approved variations claimed ({fin.variations_unclaimed_count} unclaimed)</li>
          </ul>
          <p className="mt-4 text-[11px] text-gray-500">Electrical completion is tracked separately on the Electrical Roadmap.</p>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <PanelHeading icon={Gauge} title="Base Contract Progress" />
          <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full bg-[#765297]" style={{ width: `${Math.min(100, fin.base_contract_claimed_pct)}%` }} />
          </div>
          <div data-testid="fin-progress-summary" className="mt-2 font-mono-data text-xs text-gray-600">{fin.base_contract_claimed_pct}% claimed · {fin.claims_count} claim(s)</div>
          <p className="mt-3 text-[11px] text-gray-500">Variation claims are financially separate and never alter the Base Contract percentage.</p>
        </section>
      </div>
    </div>
  );
}